import { apiClient } from '@/shared/api/http/client';

export interface SystemLogDto {
  logId: string;
  logTypeId: string;
  logType?: { logTypeId: string; logTypeName: string } | null;
  date: string;
  objectId: string;
  baseObjectId: string | null;
  objectActionId: string | null;
  keyFieldValue: string | null;
  info: string | null;
  infoAppName: string | null;
  infoUser: string | null;
  infoDetail: string | null;
  infoParams: string | null;
  infoHostName: string | null;
  durationMs: number | null;
  success: boolean;
  errorMessage: string | null;
}
const opts = { octo: { expectSuccess: false } } as const;

export interface AllLogsQuery {
  objectIds?: string[];
  logTypes?: string[];
  users?: string[];
  /** yyyy-MM-dd (gün başı) */
  fromDate?: string | null;
  /** yyyy-MM-dd (gün sonuna kadar dâhil) */
  toDate?: string | null;
  search?: string | null;
  page?: number;
  pageSize?: number;
}

/** Blazor SystemLogsLayout'un çağırdığı `GET api/data/GetAllLogs` sorgu dizesi. */
export function buildAllLogsQuery(q: AllLogsQuery): string {
  const p = new URLSearchParams();
  p.set('page', String(q.page ?? 1));
  p.set('pageSize', String(q.pageSize ?? 200));
  if (q.objectIds?.length) p.set('objectIds', q.objectIds.join(','));
  if (q.logTypes?.length) p.set('logTypes', q.logTypes.join(','));
  if (q.users?.length) p.set('users', q.users.join(','));
  if (q.fromDate) p.set('fromDate', `${q.fromDate}T00:00:00`);
  if (q.toDate) p.set('toDate', `${q.toDate}T23:59:59`);
  if (q.search?.trim()) p.set('search', q.search.trim());
  return p.toString();
}

export const logsApi = {
  row: (objectId: string, key: string) => apiClient.get<SystemLogDto[]>(`/api/data/GetLogRow/${objectId}/${encodeURIComponent(key)}`, opts),
  /**
   * Kaydın BÜTÜN geçmişi — bölünmüş tablo (split table) zinciri boyunca.
   *
   * Aynı tablonun birden çok görünümü olabiliyor (Sv_…, Pv_… hepsi Tb_…'ye dayanır) ve
   * kayıt hangi ekrandan değiştirildiyse log o ekranın nesnesiyle yazılıyor. Ekran
   * kapsamı yalnızca kendi nesnesini gösterdiği için, başka bir ekrandan yapılan
   * değişiklik geçmişte hiç görünmüyordu; bu uç kök tabloya (BaseObjectId) bakıyor.
   */
  record: (objectId: string, key: string) =>
    apiClient.get<SystemLogDto[]>(
      `/api/data/GetLogByRecord/${objectId}/${encodeURIComponent(key)}`,
      opts,
    ),
  table: (objectId: string, page = 1, pageSize = 200) => apiClient.get<SystemLogDto[]>(`/api/data/GetLogByTable/${objectId}?page=${page}&pageSize=${pageSize}`, opts),
  all: (q: AllLogsQuery) => apiClient.get<SystemLogDto[]>(`/api/data/GetAllLogs?${buildAllLogsQuery(q)}`, opts),
};

/** Log zaman damgası gösterimi (yerel saat). */
export function formatLogDate(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('tr-TR');
}

export interface ChangeItem { fieldName: string; oldValue: unknown; newValue: unknown }
const HIDDEN = new Set(['updatedate', 'updateuser', 'updateuserid', 'updateddate', 'updateduser', 'updateduserid', 'updatedby', 'adddate', 'adduser', 'adduserid', 'addeddate', 'addeduser', 'addeduserid', 'createddate', 'createdby', 'deletedate', 'deleteuser', 'deleteuserid']);

/** Blazor ParseChangesFromParams: JSON [{FieldName, OldValue, NewValue}] (audit alanları gizli). */
export function parseChanges(infoParams: string | null): ChangeItem[] {
  if (!infoParams) return [];
  try {
    const raw = JSON.parse(infoParams) as Record<string, unknown>[];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((c) => ({ fieldName: String(c['fieldName'] ?? c['FieldName'] ?? ''), oldValue: c['oldValue'] ?? c['OldValue'] ?? null, newValue: c['newValue'] ?? c['NewValue'] ?? null }))
      .filter((c) => c.fieldName && !HIDDEN.has(c.fieldName.toLowerCase()));
  } catch {
    return [];
  }
}

/** Rapor/aksiyon parametreleri: JSON dict, [{Name,Value}] ya da k=v;k=v. */
export function parseParams(raw: string | null): [string, string][] {
  if (!raw) return [];
  try {
    const j = JSON.parse(raw) as unknown;
    if (Array.isArray(j)) return j.map((p) => [String((p as Record<string, unknown>)['name'] ?? (p as Record<string, unknown>)['Name'] ?? ''), String((p as Record<string, unknown>)['value'] ?? (p as Record<string, unknown>)['Value'] ?? '')]);
    if (j && typeof j === 'object') return Object.entries(j as Record<string, unknown>).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)]);
  } catch {
    /* k=v */
  }
  if (raw.includes('=')) return raw.split(/[;,]/).map((p) => p.split('=')).filter((kv) => kv.length === 2 && kv[0]!.trim()).map((kv) => [kv[0]!.trim(), kv[1]!.trim()]);
  return [];
}
